/* eslint-disable react/no-multi-comp */
import { forwardRef, useImperativeHandle, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Button,
  Separator,
  Typography,
} from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { IconPlus, IconBlockquote } from "@tabler/icons-react";
import PromptLibraryFilters from "./PromptLibraryFilters";
import PromptsList from "./PromptsList";
import PromptForm from "./PromptForm";
import { PromptLibraryProvider, usePromptLibrary } from "./PromptLibraryContext";

export interface PromptLibraryDrawerRef {
  open: () => void;
  close: () => void;
  openWithPromptText: (promptText: string) => void;
}

interface PromptLibraryDrawerProps {
  onUsePrompt: (promptText: string) => void;
}

const PromptLibraryDrawerContent = forwardRef<PromptLibraryDrawerRef, PromptLibraryDrawerProps>(
  ({ onUsePrompt }, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const {
      isCreateOrEditPrompt,
      promptToEdit,
      prefilledPromptText,
      openCreatePrompt,
      openWithPromptText,
      closePromptForm,
    } = usePromptLibrary();

    useImperativeHandle(ref, () => ({
      open: () => setIsOpen(true),
      close: () => setIsOpen(false),
      openWithPromptText: (promptText: string) => {
        openWithPromptText(promptText);
        setIsOpen(true);
      },
    }));

    const handleOpenChange = (open: boolean) => {
      setIsOpen(open);
      if (!open) {
        closePromptForm();
      }
    };

    const handleUsePrompt = (promptText: string) => {
      onUsePrompt(promptText);
      setIsOpen(false);
    };

    return (
      <>
        <Sheet open={isOpen} onOpenChange={handleOpenChange}>
          <SheetContent
            side="right"
            // eslint-disable-next-line custom-rules/forbid-classnames
            className="!al-max-w-[70vw] al-min-w-[800px] al-flex al-flex-col"
          >
            {isCreateOrEditPrompt ? (
              <PromptForm
                prompt={promptToEdit}
                prefilledPromptText={prefilledPromptText}
                onClose={closePromptForm}
              />
            ) : (
              <>
                <SheetHeader className="al-mb-2 al-pt-1 al-px-1">
                  <Stack align={"center"} justify={"between"}>
                    <SheetTitle>
                      <Stack justify={"center"}>
                        <Stack
                          justify={"center"}
                          align={"center"}
                          className={"al-bg-blue-100 al-text-brand al-rounded-xs al-p-1"}
                        >
                          <IconBlockquote className={"al-size-5"} />
                        </Stack>
                        <Typography variant={"section-heading"}>All prompts</Typography>
                      </Stack>
                    </SheetTitle>
                    <Button size="sm" onClick={openCreatePrompt}>
                      <IconPlus className={"al-size-4"} />
                      New Saved Prompt
                    </Button>
                  </Stack>
                </SheetHeader>

                <Separator className={"al-mt-2 al-mb-4 al-w-full"} />

                <Stack direction="column" gap={"none"} className="al-flex-1 al-pb-1 al-px-1">
                  <div className="al-flex-shrink-0">
                    <PromptLibraryFilters />
                  </div>
                  <Separator className={"al-my-3"} />
                  <div className="al-flex-1">
                    <PromptsList onUsePrompt={handleUsePrompt} />
                  </div>
                </Stack>
              </>
            )}
          </SheetContent>
        </Sheet>
      </>
    );
  }
);

PromptLibraryDrawerContent.displayName = "PromptLibraryDrawerContent";

const PromptLibraryDrawer = forwardRef<PromptLibraryDrawerRef, PromptLibraryDrawerProps>(
  (props, ref) => {
    return (
      <PromptLibraryProvider>
        <PromptLibraryDrawerContent {...props} ref={ref} />
      </PromptLibraryProvider>
    );
  }
);

PromptLibraryDrawer.displayName = "PromptLibraryDrawer";

export default PromptLibraryDrawer;
